import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, ArrowRight, Home, BookOpen } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verifying, setVerifying] = useState(true);
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setVerifying(false);
    }
  }, [sessionId]);

  const verifyPayment = async () => {
    try {
      // Optionnel : vérifier le paiement côté serveur
      const response = await axios.get(`/payments/verify-session/${sessionId}`);
      setPaymentData(response.data);
      toast.success('Paiement confirmé avec succès !');
    } catch (error) {
      console.error('Erreur lors de la vérification du paiement:', error);
      // Même en cas d'erreur de vérification, on peut afficher la page de succès
      // car Stripe nous a redirigé ici, donc le paiement a réussi
    } finally {
      setVerifying(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de votre paiement...</p>
        </div>
      </div>
    );
  }

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Page non accessible</h1>
          <p className="text-gray-600 mb-6">Cette page n'est accessible qu'après un paiement réussi.</p>
          <Link to="/courses" className="btn btn-primary">
            Voir les cours
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          {/* Icône de succès */}
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>

          {/* Message de succès */}
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Paiement réussi !
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Félicitations ! Votre achat a été confirmé avec succès. 
            Vous avez maintenant accès à votre cours.
          </p>

          {/* Informations du paiement */}
          {paymentData && (
            <div className="bg-gray-50 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-semibold text-gray-900 mb-3">Détails de votre achat :</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Cours :</span>
                  <span className="font-medium">{paymentData.courseName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Montant :</span>
                  <span className="font-medium">{paymentData.amount} {paymentData.currency.toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date :</span>
                  <span className="font-medium">{new Date().toLocaleDateString('fr-FR')}</span>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-4">
            <Link
              to="/dashboard"
              className="w-full btn btn-primary flex items-center justify-center"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Accéder à mes cours
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            
            <Link
              to="/courses"
              className="w-full btn btn-secondary flex items-center justify-center"
            >
              Découvrir d'autres cours
            </Link>
            
            <Link
              to="/"
              className="w-full btn btn-outline flex items-center justify-center"
            >
              <Home className="w-5 h-5 mr-2" />
              Retour à l'accueil
            </Link>
          </div>

          {/* Informations supplémentaires */}
          <div className="mt-8 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">Prochaines étapes :</h4>
            <ul className="text-sm text-blue-800 text-left space-y-1">
              <li>• Vous recevrez un email de confirmation sous peu</li>
              <li>• Votre cours est maintenant disponible dans votre tableau de bord</li>
              <li>• Vous pouvez commencer à apprendre immédiatement</li>
              <li>• En cas de question, contactez notre support</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
